import React from 'react';

/**
 * Free-text clinical context typed by the examining clinician or health worker.
 *
 * What this does to the inference — stated here so the UI copy stays honest:
 *
 *  - **Diabetic foot ulcer** runs on a vision LLM, so this text enters the
 *    actual model prompt and genuinely changes that modality's output.
 *    "Numbness for 6 months, cannot feel the ulcer" turns an ambiguous lesion
 *    into a probable neuropathic one.
 *  - **X-ray, skin, retina, breast and fetal** screening are pure image
 *    classifiers (DenseNet / ViT). Text cannot change their predictions, and
 *    we do not pretend it does. For those, context drives the encounter
 *    synthesis, the triage priority and the recommendations — interpretation,
 *    not classification.
 *
 * The backend returns a `context_influence` block on every encounter saying
 * which of the above actually applied, and the results view renders it.
 */

export const CLINICAL_FIELDS = [
  {
    key: 'symptoms',
    label: 'Presenting symptoms',
    placeholder: 'e.g. blurred vision in both eyes; numbness in both feet; cannot feel the sore',
    rows: 2,
    hint: 'What the patient reports — including what they cannot feel.',
  },
  {
    key: 'symptomDuration',
    label: 'Duration',
    placeholder: 'e.g. 3 months',
    rows: 1,
  },
  {
    key: 'medicalHistory',
    label: 'Relevant history',
    placeholder: 'e.g. type 2 diabetes 12 years, hypertension, no prior eye screening',
    rows: 2,
  },
  {
    key: 'medications',
    label: 'Current medications',
    placeholder: 'e.g. metformin 1g BD, amlodipine 5mg',
    rows: 1,
  },
  {
    key: 'clinicalNotes',
    label: 'Examining clinician notes',
    placeholder: 'e.g. walked in barefoot; right plantar lesion noted, patient unaware of it',
    rows: 2,
    hint: 'Anything you observed that the photograph does not show.',
  },
];

const ClinicalContextFields = ({ value, onChange, defaultOpen = false }) => {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  const filled = CLINICAL_FIELDS.filter((f) => (value?.[f.key] || '').trim()).length;

  return (
    <details
      open={defaultOpen}
      className="bg-gray-800/60 border border-gray-700 rounded-lg p-4"
    >
      <summary className="cursor-pointer text-sm font-medium text-gray-300 select-none">
        Clinical context{' '}
        <span className="text-gray-500 font-normal">
          (optional — {filled > 0 ? `${filled} field${filled > 1 ? 's' : ''} filled` : 'improves the reading'})
        </span>
      </summary>

      <div className="mt-4 space-y-3">
        {CLINICAL_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
            {f.rows > 1 ? (
              <textarea
                rows={f.rows}
                value={value?.[f.key] || ''}
                onChange={set(f.key)}
                placeholder={f.placeholder}
                maxLength={600}
                className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500 resize-y"
              />
            ) : (
              <input
                type="text"
                value={value?.[f.key] || ''}
                onChange={set(f.key)}
                placeholder={f.placeholder}
                maxLength={600}
                className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500"
              />
            )}
            {f.hint && <p className="mt-1 text-[11px] text-gray-600">{f.hint}</p>}
          </div>
        ))}

        <p className="text-[11px] text-gray-500 leading-relaxed border-t border-gray-700 pt-3">
          This text is used by the diabetic foot ulcer model directly, and informs
          triage and the cross-image summary for every modality. It does not
          change the image classifiers&apos; own predictions — those read the
          image only. The results page states exactly where it was used.
        </p>
      </div>
    </details>
  );
};

export default ClinicalContextFields;
